import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Building2, User, Target, Clock, CheckCircle, ArrowRight, ArrowLeft,
  Loader2, Briefcase, Mail, Phone, Globe
} from "lucide-react";

export const ClientIntakePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const intakeToken = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [existingIntake, setExistingIntake] = useState(null);
  
  const [formData, setFormData] = useState({
    // Business Information
    business_name: "",
    business_type: "service",
    industry: "",
    years_in_business: 0,
    revenue_range: "",
    employee_count: 1,
    website: "",
    
    // Contact Information
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    preferred_contact: "email",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    
    // Business Goals
    primary_business_goal: "",
    specific_challenges: [],
    desired_outcomes: [],
    timeline_urgency: "90_days",
    
    // Current State
    current_revenue: "",
    target_revenue: "",
    biggest_bottleneck: "",
    hours_working_per_week: 40,
    ideal_hours_per_week: 25,
    
    // Consulting Expectations
    consulting_expectations: "",
    previous_consulting_experience: "",
    budget_range: "",
    commitment_level: "",
    
    // Additional
    how_did_you_hear: "",
    additional_notes: "",
    
    // Agreement
    commitment_agreed: false
  });

  const [newChallenge, setNewChallenge] = useState("");
  const [newOutcome, setNewOutcome] = useState("");

  useEffect(() => {
    fetchExistingIntake();
  }, []);

  const fetchExistingIntake = async () => {
    try {
      const res = await authFetch(`${API}/intake/my-intake`);
      if (res.ok) {
        const data = await res.json();
        if (data && Object.keys(data).length > 0) {
          setExistingIntake(data);
          setFormData(prev => ({ ...prev, ...data }));
        }
      }
    } catch (_error) {
      console.error("Error fetching intake:", _error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/intake/submit`, {
        method: "POST",
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Intake submitted successfully!");
        
        if (formData.commitment_agreed) {
          navigate("/dashboard");
        } else {
          setCurrentStep(5); // Show thank you page
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to submit intake");
      }
    } catch (_error) {
      toast.error("Failed to submit intake");
    } finally {
      setSaving(false);
    }
  };

  const addChallenge = () => {
    if (newChallenge.trim()) {
      setFormData(prev => ({
        ...prev,
        specific_challenges: [...prev.specific_challenges, newChallenge.trim()]
      }));
      setNewChallenge("");
    }
  };

  const addOutcome = () => {
    if (newOutcome.trim()) {
      setFormData(prev => ({
        ...prev,
        desired_outcomes: [...prev.desired_outcomes, newOutcome.trim()]
      }));
      setNewOutcome("");
    }
  };

  const removeChallenge = (index) => {
    setFormData(prev => ({
      ...prev,
      specific_challenges: prev.specific_challenges.filter((_, i) => i !== index)
    }));
  };

  const removeOutcome = (index) => {
    setFormData(prev => ({
      ...prev,
      desired_outcomes: prev.desired_outcomes.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const steps = [
    { num: 1, title: "Business Info", icon: Building2 },
    { num: 2, title: "Contact", icon: User },
    { num: 3, title: "Goals & Challenges", icon: Target },
    { num: 4, title: "Expectations", icon: Briefcase }
  ];

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4" data-testid="client-intake-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-neutral-900">Client Intake Form</h1>
          <p className="text-neutral-600 mt-2">
            {existingIntake ? "Update your information" : "Tell us about yourself and your business"}
          </p>
          {existingIntake?.status && (
            <div className={`inline-flex items-center mt-2 px-3 py-1 rounded-full text-sm ${
              existingIntake.status === "approved" ? "bg-green-100 text-green-700" :
              existingIntake.status === "denied" ? "bg-red-100 text-red-700" :
              existingIntake.status === "under_review" ? "bg-blue-100 text-blue-700" :
              "bg-amber-100 text-amber-700"
            }`}>
              Status: {existingIntake.status.replace("_", " ").toUpperCase()}
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8">
          {steps.map((step) => (
            <div 
              key={step.num}
              className={`flex flex-col items-center cursor-pointer ${
                currentStep === step.num ? "text-emerald-600" : 
                currentStep > step.num ? "text-emerald-500" : "text-neutral-400"
              }`}
              onClick={() => setCurrentStep(step.num)}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                currentStep === step.num ? "bg-emerald-600 text-white" :
                currentStep > step.num ? "bg-emerald-100 text-emerald-600" :
                "bg-neutral-200 text-neutral-500"
              }`}>
                {currentStep > step.num ? <CheckCircle className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
              </div>
              <span className="text-xs font-medium hidden sm:block">{step.title}</span>
            </div>
          ))}
        </div>

        <Card>
          <CardContent className="p-6">
            {/* Step 1: Business Information */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-emerald-600" />
                  Business Information
                </h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Business Name *</Label>
                    <Input
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      placeholder="Your business name"
                      data-testid="intake-business-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Business Type</Label>
                    <Select value={formData.business_type} onValueChange={(v) => setFormData({ ...formData, business_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="service">Service-Based</SelectItem>
                        <SelectItem value="product">Product-Based</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                        <SelectItem value="consulting">Consulting</SelectItem>
                        <SelectItem value="agency">Agency</SelectItem>
                        <SelectItem value="coaching">Coaching</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Industry</Label>
                    <Input
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      placeholder="e.g., Healthcare, Technology, Retail"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Years in Business</Label>
                    <Input
                      type="number"
                      value={formData.years_in_business}
                      onChange={(e) => setFormData({ ...formData, years_in_business: parseInt(e.target.value) || 0 })}
                      min={0}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Revenue Range</Label>
                    <Select value={formData.revenue_range} onValueChange={(v) => setFormData({ ...formData, revenue_range: v })}>
                      <SelectTrigger><SelectValue placeholder="Select range" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pre_revenue">Pre-Revenue</SelectItem>
                        <SelectItem value="0_50k">$0 - $50K</SelectItem>
                        <SelectItem value="50k_100k">$50K - $100K</SelectItem>
                        <SelectItem value="100k_250k">$100K - $250K</SelectItem>
                        <SelectItem value="250k_500k">$250K - $500K</SelectItem>
                        <SelectItem value="500k_1m">$500K - $1M</SelectItem>
                        <SelectItem value="1m_plus">$1M+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Number of Employees</Label>
                    <Input
                      type="number"
                      value={formData.employee_count}
                      onChange={(e) => setFormData({ ...formData, employee_count: parseInt(e.target.value) || 1 })}
                      min={1}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Website</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <Input
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://yourwebsite.com"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Contact Information */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-emerald-600" />
                  Contact Information
                </h2>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Your full name"
                      data-testid="intake-full-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="you@email.com"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Preferred Contact Method</Label>
                    <Select value={formData.preferred_contact} onValueChange={(v) => setFormData({ ...formData, preferred_contact: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="text">Text Message</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Your Timezone</Label>
                  <Input
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    placeholder="e.g., America/New_York"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Goals & Challenges */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-emerald-600" />
                  Goals & Challenges
                </h2>

                <div className="space-y-2">
                  <Label>Primary Business Goal *</Label>
                  <Textarea
                    value={formData.primary_business_goal}
                    onChange={(e) => setFormData({ ...formData, primary_business_goal: e.target.value })}
                    placeholder="What is the main goal you want to achieve with consulting?"
                    rows={3}
                    data-testid="intake-primary-goal"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Specific Challenges</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newChallenge}
                      onChange={(e) => setNewChallenge(e.target.value)}
                      placeholder="Add a challenge you're facing"
                      onKeyPress={(e) => e.key === "Enter" && addChallenge()}
                    />
                    <Button type="button" onClick={addChallenge} variant="outline">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.specific_challenges.map((challenge, idx) => (
                      <span key={idx} className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm flex items-center">
                        {challenge}
                        <button onClick={() => removeChallenge(idx)} className="ml-2 text-amber-600 hover:text-amber-800">×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Desired Outcomes</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newOutcome}
                      onChange={(e) => setNewOutcome(e.target.value)}
                      placeholder="What outcome do you want to achieve?"
                      onKeyPress={(e) => e.key === "Enter" && addOutcome()}
                    />
                    <Button type="button" onClick={addOutcome} variant="outline">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.desired_outcomes.map((outcome, idx) => (
                      <span key={idx} className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm flex items-center">
                        {outcome}
                        <button onClick={() => removeOutcome(idx)} className="ml-2 text-emerald-600 hover:text-emerald-800">×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hours Working Per Week</Label>
                    <Input
                      type="number"
                      value={formData.hours_working_per_week}
                      onChange={(e) => setFormData({ ...formData, hours_working_per_week: parseInt(e.target.value) || 40 })}
                      min={1}
                      max={168}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ideal Hours Per Week</Label>
                    <Input
                      type="number"
                      value={formData.ideal_hours_per_week}
                      onChange={(e) => setFormData({ ...formData, ideal_hours_per_week: parseInt(e.target.value) || 25 })}
                      min={1}
                      max={168}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Biggest Bottleneck</Label>
                  <Textarea
                    value={formData.biggest_bottleneck}
                    onChange={(e) => setFormData({ ...formData, biggest_bottleneck: e.target.value })}
                    placeholder="What's the biggest thing holding your business back right now?"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Timeline Urgency</Label>
                  <Select value={formData.timeline_urgency} onValueChange={(v) => setFormData({ ...formData, timeline_urgency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30_days">Urgent - Within 30 days</SelectItem>
                      <SelectItem value="90_days">Standard - Within 90 days</SelectItem>
                      <SelectItem value="6_months">Long-term - Within 6 months</SelectItem>
                      <SelectItem value="flexible">Flexible - No specific timeline</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 4: Expectations & Commitment */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <Briefcase className="w-5 h-5 mr-2 text-emerald-600" />
                  Expectations & Commitment
                </h2>

                <div className="space-y-2">
                  <Label>What do you expect from our consulting relationship?</Label>
                  <Textarea
                    value={formData.consulting_expectations}
                    onChange={(e) => setFormData({ ...formData, consulting_expectations: e.target.value })}
                    placeholder="Tell us what you're looking for in a consultant..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Previous Consulting Experience</Label>
                  <Textarea
                    value={formData.previous_consulting_experience}
                    onChange={(e) => setFormData({ ...formData, previous_consulting_experience: e.target.value })}
                    placeholder="Have you worked with consultants before? How was that experience?"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>How did you hear about us?</Label>
                  <Select value={formData.how_did_you_hear} onValueChange={(v) => setFormData({ ...formData, how_did_you_hear: v })}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="social_media">Social Media</SelectItem>
                      <SelectItem value="google">Google Search</SelectItem>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="event">Event/Workshop</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Additional Notes</Label>
                  <Textarea
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                    placeholder="Anything else you'd like us to know?"
                    rows={3}
                  />
                </div>

                {/* Commitment Agreement */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-6">
                  <h3 className="font-semibold text-emerald-900 mb-2">Commitment to Success</h3>
                  <p className="text-sm text-emerald-800 mb-4">
                    By checking this box, I acknowledge that I am committed to:
                  </p>
                  <ul className="text-sm text-emerald-700 space-y-1 mb-4">
                    <li>• Being open and honest about my business challenges</li>
                    <li>• Actively participating in the consulting process</li>
                    <li>• Implementing recommended strategies and action items</li>
                    <li>• Communicating promptly with my consultant</li>
                  </ul>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="commitment"
                      checked={formData.commitment_agreed}
                      onCheckedChange={(checked) => setFormData({ ...formData, commitment_agreed: checked })}
                      data-testid="intake-commitment-checkbox"
                    />
                    <Label htmlFor="commitment" className="text-sm font-medium cursor-pointer">
                      I agree to commit to this consulting relationship
                    </Label>
                  </div>
                </div>
              </div>
            )}

            {/* Thank You Step */}
            {currentStep === 5 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-semibold text-neutral-900 mb-2">Thank You!</h2>
                <p className="text-neutral-600 mb-6">
                  Your intake form has been submitted successfully. Our team will review your information and be in touch shortly.
                </p>
                <Button onClick={() => navigate("/dashboard")} className="bg-emerald-600 hover:bg-emerald-700">
                  Go to Dashboard
                </Button>
              </div>
            )}

            {/* Navigation Buttons */}
            {currentStep < 5 && (
              <div className="flex justify-between mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                  disabled={currentStep === 1}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                
                {currentStep < 4 ? (
                  <Button
                    onClick={() => setCurrentStep(currentStep + 1)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="intake-submit-btn"
                  >
                    {saving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                    ) : (
                      <><CheckCircle className="w-4 h-4 mr-2" /> Submit Intake</>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Back to Dashboard Link */}
        {currentStep < 5 && (
          <div className="text-center mt-4">
            <Button variant="link" onClick={() => navigate("/dashboard")} className="text-neutral-500">
              Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientIntakePage;
